import { useMemo } from 'react';
import { useNavigate } from 'react-router';
import { Box, Column, Row, Typography } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import {
  CandidateStatus,
  CandidateStatusMapper,
  ICandidateListViewEntity,
  useToggleShortlistCandidate,
} from '~/domain/PermanentJobs';
import DefaultProfileImage from '~/images/doctor.png';
import {
  ArrowRightCircleOutlineIcon,
  BookmarkOutlineIcon,
  BookmarkSolidIcon,
  ChatOutlineIcon,
  CheckCircleOutlineIcon,
  CloseCircleOutlineIcon,
} from '~/images/icons';
import {
  firePermCandidateListActionClicked,
  firePermCandidateListCandidateClicked,
  firePermCandidateListShortlistClicked,
} from '~/posthogs/events';
import { CandidateAction } from '~/posthogs/events/PermJobs/types';
import './ListItem.style.scss';

interface IListItemProps {
  data: ICandidateListViewEntity;
  jobPostingId: string;
  onHireClick?: (candidate: Pick<ICandidateListViewEntity, 'id' | 'professional'>) => void;
  onRejectClick?: (candidate: Pick<ICandidateListViewEntity, 'id' | 'professional'>) => void;
}

export const ListItem = ({
  data: {
    createdAt,
    id,
    isShortlisted,
    professional,
    officeStatus,
    onsiteInterviewId,
    yearsOfExperience,
  },
  jobPostingId,
  onHireClick,
  onRejectClick,
}: IListItemProps) => {
  const navigate = useNavigate();

  const { mutate: doShortlistCandidate, isPending: isShortlistCandidatePending } =
    useToggleShortlistCandidate({
      candidateId: id,
      isShortlisted,
    });

  const isHired = officeStatus === CandidateStatus.Hired;
  const isInterviewed = officeStatus === CandidateStatus.Interviewed;
  const isRejected = officeStatus === CandidateStatus.Rejected;
  const isScheduled = officeStatus === CandidateStatus.Scheduled;

  const canShortlist = !isHired && !isRejected;

  const handleRedirectToProfessionalProfile = () => {
    // Track candidate card click
    firePermCandidateListCandidateClicked({
      postingId: jobPostingId,
      professionalId: professional.id,
      candidateStatus: officeStatus,
    });
    navigate(`/dashboard/professional/${professional.id}`);
  };

  const StatusActions = useMemo(() => {
    if (isHired) {
      return (
        <Typography as="span" bold size="body_large" style={{ marginLeft: 40 }}>
          Hired
        </Typography>
      );
    }

    if (isRejected) {
      return (
        <Typography as="span" bold size="body_large" style={{ marginLeft: 40 }}>
          Rejected
        </Typography>
      );
    }

    if (isScheduled) {
      return (
        <button
          className="btn-message"
          data-cy="candidate-action-message"
          onClick={() => {
            // Track the message action
            firePermCandidateListActionClicked({
              postingId: jobPostingId,
              professionalId: professional.id,
              actionName: CandidateAction.Message,
            });
            navigate(`/dashboard/chat/${onsiteInterviewId}`);
          }}
        >
          <ChatOutlineIcon color={colors.secondary[500]} />
          <Typography as="span" bold size="body_large">
            Message
          </Typography>
        </button>
      );
    }

    return (
      <>
        <button
          className="btn-reject"
          data-cy="candidate-action-reject"
          onClick={() => {
            // Track the reject action
            firePermCandidateListActionClicked({
              postingId: jobPostingId,
              professionalId: professional.id,
              actionName: CandidateAction.Reject,
            });
            onRejectClick?.({ id, professional });
          }}
        >
          <CloseCircleOutlineIcon color={colors.neutral[400]} />
          <Typography as="span" bold size="body_large">
            Reject
          </Typography>
        </button>
        {isInterviewed ? (
          <button
            className="btn-hire"
            data-cy="candidate-action-hire"
            onClick={() => {
              // Track the hire action
              firePermCandidateListActionClicked({
                postingId: jobPostingId,
                professionalId: professional.id,
                actionName: CandidateAction.Hire,
              });
              onHireClick?.({ id, professional });
            }}
          >
            <CheckCircleOutlineIcon />
            <Typography as="span" bold size="body_large">
              Hire
            </Typography>
          </button>
        ) : (
          <button
            className="btn-review"
            data-cy="candidate-action-review"
            onClick={() => {
              // Track the review action
              firePermCandidateListActionClicked({
                postingId: jobPostingId,
                professionalId: professional.id,
                actionName: CandidateAction.Review,
              });
              navigate(`/dashboard/job/${onsiteInterviewId}/confirm/${id}`);
            }}
          >
            <ArrowRightCircleOutlineIcon color={colors.secondary[500]} />
            <Typography as="span" bold size="body_large">
              Review
            </Typography>
          </button>
        )}
      </>
    );
  }, [
    isHired,
    isRejected,
    isScheduled,
    isInterviewed,
    navigate,
    onsiteInterviewId,
    onRejectClick,
    id,
    professional,
    onHireClick,
    jobPostingId,
  ]);

  return (
    <Box
      className="grid items-center cursor-pointer shadow-xs hover__shadow-sm bg-shades-0 rounded-field"
      data-cy="pj-candidate-list-item"
      id="pj-candidate-list-item"
      key={id}
      py={3}
      style={{ gridTemplateColumns: '96px 1fr 15% 12% 25%' }}
    >
      <Column
        align="end"
        justify="center"
        height="100%"
        style={{ cursor: 'pointer' }}
        onClick={handleRedirectToProfessionalProfile}
      >
        <Column
          align="center"
          justify="center"
          height="48px"
          width="48px"
          className="rounded-full"
          style={{ backgroundColor: '#DFEBED', justifySelf: 'flex-end', marginRight: 16 }}
        >
          {professional.picture ? (
            <img
              src={professional.picture}
              height="100%"
              width="100%"
              style={{
                borderRadius: 100,
                objectFit: 'cover',
              }}
            />
          ) : (
            <img src={DefaultProfileImage} height={36} width={36} />
          )}
        </Column>
      </Column>
      <Column style={{ cursor: 'pointer' }} onClick={handleRedirectToProfessionalProfile}>
        <Typography as="span" bold size="body_large" style={{ textDecoration: 'underline' }}>
          {professional.name}
        </Typography>
        <Typography as="span" color={colors.neutral[500]}>
          <Typography as="span" bold color={colors.neutral[500]}>
            Experience
          </Typography>
          : {yearsOfExperience} years
        </Typography>
        <Typography as="span" color={colors.neutral[500]}>
          <Typography as="span" bold color={colors.neutral[500]}>
            Applied on
          </Typography>
          : {createdAt}
        </Typography>
      </Column>
      <Typography as="span" color={colors.neutral[500]} data-cy="candidate-process-step">
        {CandidateStatusMapper.toProcessStep(officeStatus)}
      </Typography>
      <button
        disabled={!canShortlist || isShortlistCandidatePending}
        style={{
          background: 'transparent',
        }}
        onClick={() => {
          // Track shortlist click
          firePermCandidateListShortlistClicked({
            postingId: jobPostingId,
            isShortlisted: !isShortlisted, // The new state after toggle
          });
          doShortlistCandidate();
        }}
      >
        {isShortlisted ? (
          <BookmarkSolidIcon color={canShortlist ? colors.secondary[500] : colors.secondary[200]} />
        ) : (
          <BookmarkOutlineIcon
            color={canShortlist ? colors.secondary[500] : colors.secondary[200]}
          />
        )}
      </button>
      <Row gap={3}>{StatusActions}</Row>
    </Box>
  );
};
